import { Fragment, useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Container } from '@/components/container';
import { KeenIcon } from '@/components';
import { triggerDownload } from '@/utils';
import auditLogService from '@/services/security/auditLogService';
import { formatWhen } from '@/utils/formatWhen';
import { actionBadge, detailRows, toParams } from './auditLogFormat';

const EMPTY_FILTERS = { user: '', action: '', table: '', from: '', to: '', search: '' };

/**
 * Audit Log (OL-156): everything the trail records across the firm — record
 * changes, sign-ins, exports, downloads, bulk deletes and role changes — for
 * administrators. Filter, page through, open an entry, or export as CSV.
 */
const AuditLog = () => {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [options, setOptions] = useState({ users: [], actions: [], tables: [] });
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    auditLogService.filters()
      .then(({ data }) => setOptions({ users: data?.users ?? [], actions: data?.actions ?? [], tables: data?.tables ?? [] }))
      .catch(() => toast.error('Could not load the audit log filters.'));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await auditLogService.list(toParams(applied, page));
      setRows(data?.rows ?? []);
      setMeta(data?.meta ?? { current_page: 1, last_page: 1, total: 0 });
    } catch (error) {
      toast.error('Could not load the audit log.', { description: error?.message });
    } finally {
      setLoading(false);
    }
  }, [applied, page]);

  useEffect(() => {
    load();
  }, [load]);

  const setFilter = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));

  const apply = () => {
    setPage(1);
    setApplied(filters);
  };

  const reset = () => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
    setApplied(EMPTY_FILTERS);
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const blob = await auditLogService.exportCsv(toParams(applied));
      triggerDownload(blob, `audit-log-${new Date().toISOString().slice(0, 10)}.csv`);
      toast.success('Audit log exported.');
    } catch {
      toast.error('Could not export the audit log.');
    } finally {
      setExporting(false);
    }
  };

  const select = (key, list, placeholder) => (
    <select className="select select-sm" value={filters[key]} onChange={setFilter(key)}>
      <option value="">{placeholder}</option>
      {list.map((o) => (
        <option key={o.value ?? o.id} value={o.value ?? o.id}>{o.label ?? o.name}</option>
      ))}
    </select>
  );

  return (
    <Container>
      <div className="card min-w-full mb-4">
        <div className="card-header flex-wrap gap-2">
          <div>
            <h3 className="card-title">Audit Log</h3>
            <p className="text-xs text-gray-500 mt-0.5 max-w-[640px]">
              Who changed, viewed, exported or deleted what, and when. Includes sign-ins, failed
              sign-ins, role and permission changes. Only administrators can see this page.
            </p>
          </div>
          <button className="btn btn-sm btn-light" onClick={exportCsv} disabled={exporting}>
            <KeenIcon icon="file-down" /> {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>

        <div className="card-body flex flex-wrap gap-2 items-end">
          <div className="flex flex-col gap-1 min-w-44">
            <span className="text-2xs text-gray-600">Person</span>
            {select('user', options.users, 'Everyone')}
          </div>
          <div className="flex flex-col gap-1 min-w-40">
            <span className="text-2xs text-gray-600">Action</span>
            {select('action', options.actions, 'Any action')}
          </div>
          <div className="flex flex-col gap-1 min-w-40">
            <span className="text-2xs text-gray-600">Record type</span>
            {select('table', options.tables, 'Any record')}
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-2xs text-gray-600">From</span>
            <input type="date" className="input input-sm" value={filters.from} onChange={setFilter('from')} />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-2xs text-gray-600">To</span>
            <input type="date" className="input input-sm" value={filters.to} onChange={setFilter('to')} />
          </div>
          <div className="flex flex-col gap-1 grow min-w-44">
            <span className="text-2xs text-gray-600">Search</span>
            <input
              className="input input-sm"
              placeholder="Name, email, invoice number..."
              value={filters.search}
              onChange={setFilter('search')}
              onKeyDown={(e) => e.key === 'Enter' && apply()}
            />
          </div>
          <button className="btn btn-sm btn-primary" onClick={apply}>Apply</button>
          <button className="btn btn-sm btn-light" onClick={reset}>Reset</button>
        </div>

        <div className="card-table scrollable-x-auto">
          <table className="table align-middle text-sm text-gray-700">
            <thead>
              <tr>
                <th className="text-start">When</th>
                <th className="text-start">Who</th>
                <th className="text-start">Action</th>
                <th className="text-start">Record</th>
                <th className="text-start">Details</th>
                <th className="text-start">IP address</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="text-center text-gray-500 py-6">Loading...</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={6} className="text-center text-gray-500 py-6">Nothing matches these filters.</td></tr>
              )}
              {!loading && rows.map((row) => (
                <Fragment key={row.id}>
                  <tr className="cursor-pointer hover:bg-gray-50" onClick={() => setOpenId(openId === row.id ? null : row.id)}>
                    <td className="whitespace-nowrap">{formatWhen(row.when)}</td>
                    <td>{row.actor}</td>
                    <td><span className={`badge badge-sm badge-outline ${actionBadge(row.action)}`}>{row.action_label}</span></td>
                    <td>{row.record_type || '-'}</td>
                    <td className="max-w-96 truncate" title={row.summary}>{row.summary || '-'}</td>
                    <td className="whitespace-nowrap">{row.ip || '-'}</td>
                  </tr>
                  {openId === row.id && (
                    <tr>
                      <td colSpan={6} className="bg-gray-50">
                        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs py-2">
                          {detailRows(row.details).map(({ label, value }) => (
                            <div key={label} className="flex gap-2">
                              <span className="text-gray-500 min-w-32">{label}</span>
                              <span className="text-gray-900 break-all">{value}</span>
                            </div>
                          ))}
                          {row.user_agent && (
                            <div className="flex gap-2 sm:col-span-2">
                              <span className="text-gray-500 min-w-32">Browser</span>
                              <span className="text-gray-900 break-all">{row.user_agent}</span>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card-footer justify-between text-xs text-gray-600">
          <span>{meta.total} entries</span>
          <div className="flex items-center gap-2">
            <button className="btn btn-sm btn-light" disabled={loading || page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
            <span>Page {meta.current_page} of {meta.last_page}</span>
            <button className="btn btn-sm btn-light" disabled={loading || page >= meta.last_page} onClick={() => setPage((p) => p + 1)}>Next</button>
          </div>
        </div>
      </div>
    </Container>
  );
};

export { AuditLog };
